import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Music2, Mic2 } from "lucide-react";

const Reveal = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay }}>{children}</motion.div>
);

const bandcamp = (id) => `https://bandcamp.com/EmbeddedPlayer/track=${id}/size=large/bgcol=101216/linkcol=c8a97e/tracklist=false/artwork=small/transparent=true/`;
const spotify = (id) => `https://open.spotify.com/embed/track/${id}?utm_source=generator&theme=0`;
const soundcloud = "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/soundcloud%253Atracks%253A138418656&color=%23c8a97e&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=false";

const ARTISTS = [
  {
    key: "carhole",
    name: "Carhole Philosophers",
    tag: "Vocalist · Songwriter",
    location: "Edmonton, Alberta",
    icon: Mic2,
    blurb: "An independent alternative, folk, and progressive project based out of Edmonton, Alberta, that became our own form of therapy. No chasing algorithms or trends. Just focusing on the craft and sharing songs we care about. Thanks for listening.",
    links: [
      { label: "Bandcamp", url: "https://carholephilosophers.bandcamp.com/" },
      { label: "Instagram", url: "https://www.instagram.com/carholephilosophers/" },
    ],
    embeds: [
      { title: "Nth", type: "bandcamp", src: bandcamp("225804127"), h: 120 },
      { title: "Still Trying", type: "bandcamp", src: bandcamp("280677715"), h: 120 },
    ],
  },
  {
    key: "evo",
    name: "Evo",
    tag: "Producer · Engineer",
    location: "Original music, produced & mastered by me",
    icon: Music2,
    blurb: "Two decades of making music — from experimental electronic to cinematic soundscapes. Everything here was written, produced, and mastered by me, so consider it a working sample of the production and mastering I bring to client projects. A curated handful of favorites below; dig into the full catalog on Spotify and SoundCloud.",
    links: [
      { label: "Spotify", url: "https://open.spotify.com/artist/1SwIbBP3t96N1I2TufCZC3" },
      { label: "SoundCloud", url: "https://soundcloud.com/evo9897" },
    ],
    embeds: [
      { title: "Lost and Sound Box", type: "spotify", src: spotify("0sJHhLE6xow3PoASUBRDgB"), h: 152 },
      { title: "Game Changer", type: "spotify", src: spotify("58tIkBrQltK08AbwfM3lus"), h: 152 },
      { title: "Invigorate", type: "spotify", src: spotify("6Gh8fV6w7lLTfmhf2jlH3b"), h: 152 },
      { title: "Magellan Tear", type: "spotify", src: spotify("7d4wYvO1nWd0thCHXbZqAy"), h: 152 },
      { title: "Fatboy Slim — Ya Mama (eVo Remix)", type: "soundcloud", src: soundcloud, h: 166 },
    ],
  },
];

export const MusicSection = () => (
  <section id="music" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
    <Reveal>
      <div className="mb-14 max-w-2xl">
        <span className="font-mono text-xs tracking-[0.3em] text-[#C8A97E] uppercase">The Music</span>
        <h2 className="font-head text-4xl md:text-5xl font-bold tracking-tight mt-3">Original Music</h2>
        <p className="text-[#A1A1AA] mt-4 text-base leading-relaxed">Singer, producer and engineer — two projects, one ear for detail. This is also a live sample of the production &amp; mastering I bring to client work.</p>
      </div>
    </Reveal>

    <div className="space-y-16">
      {ARTISTS.map((a, ai) => {
        const Icon = a.icon;
        return (
          <Reveal key={a.key} delay={ai * 0.05}>
            <div data-testid={`music-artist-${a.key}`} className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-md bg-[#C8A97E]/10 border border-[#C8A97E]/30 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-[#C8A97E]" />
                  </div>
                  <div>
                    <h3 className="font-head text-2xl font-medium leading-none">{a.name}</h3>
                    <span className="text-xs font-mono uppercase tracking-wider text-[#C8A97E]">{a.tag}</span>
                  </div>
                </div>
                <p className="text-xs text-[#8b8b93] mt-3">{a.location}</p>
                <p className="text-sm text-[#A1A1AA] mt-4 leading-relaxed">{a.blurb}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {a.links.map((l, i) => (
                    <a key={l.url || l.label || i} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`music-link-${a.key}-${i}`}
                      className="inline-flex items-center gap-1.5 text-xs bg-[#C8A97E] text-[#0B0C10] font-semibold px-3 py-1.5 rounded-sm hover:bg-[#B3956D] transition-colors">
                      <ExternalLink size={13} /> {l.label}
                    </a>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4 content-start">
                {a.embeds.map((e, i) => (
                  <div key={e.src || e.title || i} className={`rounded-lg overflow-hidden border border-white/10 bg-[#12141A] ${e.type === "bandcamp" ? "sm:col-span-1" : ""}`} data-testid={`music-embed-${a.key}-${i}`}>
                    <iframe title={e.title} src={e.src} width="100%" height={e.h} frameBorder="0"
                      loading="lazy" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      style={{ border: 0, display: "block" }} seamless />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);
